import { Account } from "~/bff/types/Account";
import type { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { Store } from "~/bff/types/Store";
import {
  DataLayerData,
  DataLayerDataTypes,
  EventTypes,
  EventsNames,
  NewDataLayerDataTypes,
  NewProductDataLayerData,
  ProductDataLayerData,
} from "~/constants/data-layer";
import { getCurrencyCodeByLocale, LOCALES } from "~/constants/i18n";
import { getCountryCode } from "~/helpers/country/get-country-code";
import {
  getDisplayPriceWithoutCurrency,
  getPriceWithFixedPoint,
} from "~/helpers/get-display-price";
import { getGAClearanceAttributes } from "~/helpers/get-ga-clearance-attributes";
import { getPageCategoriesTitles } from "~/helpers/get-page-categories-titles";

import { clickAndCollectOverallStatusGA } from "../../product-availability-block/helpers/click-and-collect-overall-status-ga";
import { clickAndCollectProductStatusGA } from "../../product-availability-block/helpers/click-and-collect-product-status-ga";
import { clickAndCollectStoreStatusGA } from "../../product-availability-block/helpers/click-and-collect-store-status-ga";
import { inStoreStatusGA } from "../../product-availability-block/helpers/in-store-status-ga";
import { ProductCardProps } from "../types";

interface AnalyticsData {
  account: Account | undefined;
  brand: string | undefined;
  pageLocale: LOCALES;
  position: number;
  product: ProductCardProps["product"];
  store: Store | undefined | null;
  statusKeyForInStoreStatus: string;
}

type DataForGAClicks = (
  analyticsData: AnalyticsData,
  breadcrumbs: BreadcrumbsItem[] | undefined,
) => DataLayerDataTypes;

export const getDataForGAClicks: DataForGAClicks = (analyticsData, breadcrumbs) => {
  const { brand, pageLocale, position, product } = analyticsData;
  const country = getCountryCode(pageLocale);
  const { pageCategory, pageSubCategory, pageSubSubCategory } =
    getPageCategoriesTitles(breadcrumbs);
  const currency = getCurrencyCodeByLocale(pageLocale);

  return {
    [DataLayerData.event]: EventsNames.ecommerceClicks,
    [DataLayerData.siteCountry]: country,
    [DataLayerData.pageCategory]: pageCategory,
    [DataLayerData.pageSubCategory]: pageSubCategory,
    [DataLayerData.pageSubSubCategory]: pageSubSubCategory,
    [DataLayerData.ecommerce]: {
      [DataLayerData.currencyCode]: currency,
      [DataLayerData.click]: {
        [DataLayerData.products]: [
          {
            [ProductDataLayerData.name]: product?.name ?? "",
            [ProductDataLayerData.id]: product?.productCode ?? "",
            [ProductDataLayerData.brand]: brand,
            [ProductDataLayerData.category]: pageCategory,
            [ProductDataLayerData.position]: position,
            [ProductDataLayerData.variant]: product?.displayColor ?? "",
            [ProductDataLayerData.dimension1]: pageSubCategory,
            [ProductDataLayerData.dimension2]: undefined,
          },
        ],
      },
    },
  };
};

type DataForGAAddToBag = (
  analyticsData: AnalyticsData,
  isClearancePriceEnabled?: boolean,
) => NewDataLayerDataTypes;

export const getDataForGAAddToBag: DataForGAAddToBag = (
  analyticsData,
  isClearancePriceEnabled,
) => {
  const {
    account,
    brand,
    pageLocale,
    position,
    product,
    store,
    statusKeyForInStoreStatus,
  } = analyticsData;
  const country = getCountryCode(pageLocale);
  const stockStatus = inStoreStatusGA(statusKeyForInStoreStatus, store);
  const currency = getCurrencyCodeByLocale(pageLocale);
  const priceForGA = getDisplayPriceWithoutCurrency(product?.masterPrice);
  const value = priceForGA ? +priceForGA : undefined;
  const isProductClickAndCollect =
    product?.clickAndCollectExclusive || product?.clickAndCollect;

  return {
    [DataLayerData.event]: EventsNames.addToCart,
    [DataLayerData.siteCountry]: country,
    [DataLayerData.type]: EventTypes.addToBag,
    [DataLayerData.pageCategory]: "Shopping List",
    [DataLayerData.pageSubCategory]: undefined,
    [DataLayerData.pageSubSubCategory]: undefined,
    [DataLayerData.loggedInStatus]: !!account,
    [DataLayerData.storeSelected]: store?.geomodifier ?? undefined,
    [DataLayerData.productId]: product?.productCode ?? undefined,
    [DataLayerData.productName]: product?.name ?? undefined,
    [DataLayerData.ecommerce]: {
      [DataLayerData.currency]: currency,
      [DataLayerData.value]: isClearancePriceEnabled
        ? getPriceWithFixedPoint(product?.rawClearancePrice || product?.rawPrice)
        : value,
      [DataLayerData.items]: [
        {
          [NewProductDataLayerData.itemId]: product?.productCode,
          [NewProductDataLayerData.itemName]: product?.name,
          [NewProductDataLayerData.index]: position,
          [NewProductDataLayerData.itemBrand]: brand,
          [NewProductDataLayerData.itemCategory]: null,
          [NewProductDataLayerData.itemCategory2]: null,
          [NewProductDataLayerData.itemCategory3]: null,
          [NewProductDataLayerData.itemCategory4]: null,
          [NewProductDataLayerData.itemCategory5]: clickAndCollectOverallStatusGA(
            product,
            store,
          ),
          [NewProductDataLayerData.dimension45]: store?.geomodifier,
          [NewProductDataLayerData.dimension49]: clickAndCollectStoreStatusGA(store),
          [NewProductDataLayerData.dimension50]: isProductClickAndCollect,
          [NewProductDataLayerData.dimension51]: clickAndCollectOverallStatusGA(
            product,
            store,
          ),
          [NewProductDataLayerData.dimension52]: stockStatus,
          [NewProductDataLayerData.dimension53]:
            clickAndCollectProductStatusGA(product),
          [NewProductDataLayerData.price]: priceForGA ? +priceForGA : undefined,
          ...(isClearancePriceEnabled
            ? getGAClearanceAttributes({
                regular: product?.rawPrice,
                clearance: product?.rawClearancePrice,
              })
            : {}),
          [NewProductDataLayerData.quantity]: 1,
          [NewProductDataLayerData.variant]: product?.displayColor,
          [NewProductDataLayerData.dimension2]: product?.displaySize,
          [NewProductDataLayerData.item_shop_set]: false,
        },
      ],
    },
  };
};
